import { useState } from 'react';
import { getOpDef } from '@capture/shared';
import type { FormFieldDef, TransformValidationError } from '@capture/shared';
import type { TransformPluginRow } from '@/features/admin/transform-plugins/api/transform-plugins-client';
import { pluginRowToPickerOp } from '../lib/plugin-ops';
import styles from './TransformStepConfig.module.css';

interface Props {
  opId: string;
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  errors: TransformValidationError[];
  /**
   * Published tenant transform plugins visible to this chain (F-043). When
   * `opId` doesn't match a built-in op, this list is checked by `pluginId`
   * so the step still renders its real form/description instead of
   * "Unknown op". Optional — callers that don't pass it just lose plugin
   * resolution (e.g. contexts where plugin fetch isn't relevant).
   */
  plugins?: TransformPluginRow[];
}

export function TransformStepConfig({ opId, config, onChange, errors, plugins }: Props) {
  const opDef = getOpDef(opId);
  const pluginRow = !opDef ? plugins?.find((p) => p.pluginId === opId) : undefined;
  const pluginOp = pluginRow ? pluginRowToPickerOp(pluginRow) : undefined;
  if (!opDef && !pluginOp) return <div>Unknown op: {opId}</div>;

  const description = (opDef ?? pluginOp)!.description;
  const formSchema: FormFieldDef[] = (opDef ?? pluginOp)!.formSchema;

  function handleField(key: string, value: unknown) {
    onChange({ ...config, [key]: value });
  }

  function errorFor(field: string) {
    return errors.find((e) => e.field === field || e.field.startsWith(`${field}.`));
  }

  return (
    <div className={styles.form}>
      <p className={styles.description}>{description}</p>
      {formSchema.map((field) => (
        <FieldRenderer
          key={field.key}
          field={field}
          value={config[field.key]}
          onChange={(v) => handleField(field.key, v)}
          error={errorFor(field.key)}
        />
      ))}
    </div>
  );
}

interface FieldProps {
  field: FormFieldDef;
  value: unknown;
  onChange: (next: unknown) => void;
  error?: TransformValidationError;
}

function FieldRenderer({ field, value, onChange, error }: FieldProps) {
  const id = `tsc-field-${field.key}`;
  return (
    <div className={styles.field}>
      <label
        htmlFor={id}
        className={field.required ? `${styles.label} ${styles.labelRequired}` : styles.label}
      >
        {field.label}
      </label>
      {renderInput(field, id, value, onChange)}
      {field.helpText && <p className={styles.helpText}>{field.helpText}</p>}
      {error && <p className={styles.errorText}>{error.message}</p>}
    </div>
  );
}

function TextInput({ id, value, onChange, placeholder }: { id: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  const [local, setLocal] = useState(value);
  return (
    <input
      id={id}
      type="text"
      value={local}
      onChange={(e) => { setLocal(e.target.value); onChange(e.target.value); }}
      placeholder={placeholder}
    />
  );
}

function NumberInput({ id, value, onChange }: { id: string; value: string; onChange: (v: number | undefined) => void }) {
  const [local, setLocal] = useState(value);
  return (
    <input
      id={id}
      type="number"
      value={local}
      onChange={(e) => { setLocal(e.target.value); onChange(e.target.value === '' ? undefined : Number(e.target.value)); }}
    />
  );
}

function renderInput(field: FormFieldDef, id: string, value: unknown, onChange: (next: unknown) => void) {
  switch (field.type) {
    case 'text':
      return (
        <TextInput
          id={id}
          value={(value as string) ?? ''}
          onChange={onChange}
          placeholder={field.placeholder}
        />
      );
    case 'number':
      return (
        <NumberInput
          id={id}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={onChange}
        />
      );
    case 'select':
      return (
        <select id={id} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)}>
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    case 'lookup-table':
      return <LookupTableEditor value={value as Array<{ match: string; output: string }>} onChange={onChange} />;
    case 'condition':
      return <ConditionEditor value={value as { op: string; value?: string }} onChange={onChange} />;
  }
}

interface Entry { match: string; output: string; }

function LookupTableEditor({ value, onChange }: { value?: Entry[]; onChange: (next: Entry[]) => void }) {
  const entries = value ?? [];
  function update(i: number, patch: Partial<Entry>) {
    const next = [...entries];
    next[i] = { ...(next[i] ?? { match: '', output: '' }), ...patch };
    onChange(next);
  }
  function add() { onChange([...entries, { match: '', output: '' }]); }
  function remove(i: number) { onChange(entries.filter((_, j) => j !== i)); }

  return (
    <div className={styles.tableEditor} data-testid="lookup-table-editor">
      <table className={styles.table}>
        <thead><tr><th>Match</th><th>Output</th><th></th></tr></thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={i}>
              <td><input value={e.match} onChange={(ev) => update(i, { match: ev.target.value })} /></td>
              <td><input value={e.output} onChange={(ev) => update(i, { output: ev.target.value })} /></td>
              <td><button type="button" onClick={() => remove(i)}>×</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" onClick={add}>+ Add entry</button>
    </div>
  );
}

function ConditionEditor({ value, onChange }: { value?: { op: string; value?: string }; onChange: (next: { op: string; value?: string }) => void }) {
  const v = value ?? { op: 'eq', value: '' };
  return (
    <div className={styles.condition} data-testid="condition-editor">
      <select value={v.op} onChange={(e) => onChange({ ...v, op: e.target.value })}>
        <option value="eq">equals</option>
        <option value="neq">not equals</option>
        <option value="gt">greater than</option>
        <option value="lt">less than</option>
        <option value="empty">is empty</option>
      </select>
      {v.op !== 'empty' && (
        <input type="text" value={v.value ?? ''} onChange={(e) => onChange({ ...v, value: e.target.value })} placeholder="value" />
      )}
    </div>
  );
}
